import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  Store,
  Package,
  Megaphone,
  Activity,
} from 'lucide-react';
import { Card } from '@atoms/Card';
import { Badge } from '@atoms/Badge';
import { Spinner } from '@atoms/Spinner';
import { Button } from '@atoms/Button';
import { adminService } from '@services/adminService';
import { formatCurrency, formatShortDate, ORDER_STATUS_LABELS } from '@utils/formatters';

function KpiCard({
  label,
  value,
  icon: Icon,
  hint,
}: {
  label: string;
  value: string | number;
  icon: typeof Users;
  hint?: string;
}) {
  return (
    <Card className="card-elevated flex items-start gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-sm text-slate-500">{label}</p>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
      </div>
    </Card>
  );
}

export function OverviewSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => adminService.getOverview(),
    refetchInterval: 60000,
  });

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Genel Bakış</h1>
        <p className="mt-1 text-sm text-slate-500">Platform özeti ve hızlı aksiyonlar</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Toplam Kullanıcı" value={data?.users.total ?? 0} icon={Users} />
        <KpiCard
          label="Toptancı / Restoran"
          value={`${data?.users.wholesaler ?? 0} / ${data?.users.restaurant ?? 0}`}
          icon={Store}
        />
        <KpiCard
          label="Siparişler"
          value={data?.orders.total ?? 0}
          icon={Package}
          hint={`${data?.orders.pending ?? 0} bekleyen · ${data?.orders.today ?? 0} bugün`}
        />
        <KpiCard
          label="Onay Bekleyen Reklam"
          value={data?.pendingAds ?? 0}
          icon={Megaphone}
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <Link to="/admin/ads">
          <Button variant="outline">Reklam Kuyruğu</Button>
        </Link>
        <Link to="/admin/whatsapp">
          <Button variant="outline">WhatsApp Merkezi</Button>
        </Link>
        <Link to="/admin/users">
          <Button variant="outline">Yeni Kullanıcı</Button>
        </Link>
        <Link to="/admin/system">
          <Button variant="outline">Sistem Durumu</Button>
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="card-elevated">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Son Siparişler</h2>
          </div>
          {!data?.recentOrders.length ? (
            <p className="text-sm text-slate-500">Henüz sipariş yok</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.recentOrders.map((order) => (
                <li key={order.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">
                      #{order.id.slice(0, 8)} · {order.restaurantName ?? 'Restoran'}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {order.wholesalerName ?? 'Toptancı'} · {formatShortDate(order.createdAt)}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <Badge>{ORDER_STATUS_LABELS[order.status]}</Badge>
                    <p className="mt-1 text-sm font-medium">{formatCurrency(order.totalPrice)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="card-elevated">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Son Bildirimler</h2>
            <Link to="/admin/whatsapp" className="text-sm text-brand-600 hover:underline">
              WhatsApp
            </Link>
          </div>
          {!data?.recentNotificationLogs.length ? (
            <p className="text-sm text-slate-500">Bildirim kaydı yok</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.recentNotificationLogs.map((log) => (
                <li key={log.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">{log.recipientPhone}</p>
                    <p className="text-xs text-slate-500">{formatShortDate(log.createdAt)}</p>
                  </div>
                  <Badge variant={log.status === 'sent' ? 'success' : 'warning'}>{log.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="card-elevated flex items-center gap-3">
        <Activity className={data?.systemHealthy ? 'text-emerald-600' : 'text-amber-600'} />
        <div>
          <p className="font-medium text-slate-900">
            {data?.systemHealthy ? 'Tüm servisler çalışıyor' : 'Bazı servislerde sorun olabilir'}
          </p>
          <p className="text-sm text-slate-500">
            Veritabanı, Redis, Meilisearch ve WhatsApp durumunu Sistem sekmesinden izleyin.
          </p>
        </div>
        <Link to="/admin/system" className="ml-auto">
          <Button size="sm" variant="outline">
            Detay
          </Button>
        </Link>
      </Card>
    </div>
  );
}
